import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useChannelContext } from '../context/ChannelContext';
import { categoryLabels } from '../data/channels';
import Toast from '../components/Toast';

const AdminDashboard = () => {
  const { day } = useParams();
  const navigate = useNavigate();
  const {
    channels,
    addChannel,
    updateChannel,
    deleteChannel,
    getLiveChannels,
    getWorldCupChannels
  } = useChannelContext();

  const [activeTab, setActiveTab] = useState('channels');
  const [toastMessage, setToastMessage] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    cat: 'worldcup',
    icon: '📺',
    desc: '',
    status: 'live',
    type: 'hls'
  });

  // Check admin session
  useEffect(() => {
    const isAdmin = localStorage.getItem('streambd_admin');
    const adminDay = localStorage.getItem('streambd_admin_day');
    const today = String(new Date().getDate()).padStart(2, '0');
    
    if (!isAdmin || adminDay !== day || adminDay !== today) {
      navigate('/admin/login');
    }
  }, [day, navigate]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 2800);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      showToast('Channel name is required!');
      return;
    }

    if (editingId) {
      updateChannel(editingId, formData);
      showToast('✅ Channel updated!');
      setEditingId(null);
    } else {
      addChannel(formData);
      showToast('✅ Channel added!');
    }

    setFormData({
      name: '',
      url: '',
      cat: 'worldcup',
      icon: '📺',
      desc: '',
      status: 'live',
      type: 'hls'
    });
    setActiveTab('channels');
  };

  const handleEdit = (channel) => {
    setFormData({
      name: channel.name,
      url: channel.url || '',
      cat: channel.cat,
      icon: channel.icon || '📺',
      desc: channel.desc || '',
      status: channel.status || 'live',
      type: channel.type || 'hls'
    });
    setEditingId(channel.id);
    setActiveTab('add');
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this channel?')) {
      deleteChannel(id);
      showToast('🗑️ Channel deleted');
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      name: '',
      url: '',
      cat: 'worldcup',
      icon: '📺',
      desc: '',
      status: 'live',
      type: 'hls'
    });
    setEditingId(null);
    setActiveTab('channels');
  };

  const getStats = () => {
    const total = channels.length;
    const live = getLiveChannels().length;
    const worldcup = getWorldCupChannels().length;
    const cats = [...new Set(channels.map(c => c.cat))].length;
    return { total, live, worldcup, cats };
  };

  const stats = getStats();

  const renderChannelList = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {channels.map(ch => (
        <div key={ch.id} style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{ fontSize: '24px', width: '40px', textAlign: 'center' }}>
            {ch.icon && ch.icon.startsWith('http') ? '📺' : (ch.icon || '📺')}
          </div>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              {ch.name}
              <span style={{
                fontSize: '11px',
                padding: '3px 8px',
                borderRadius: '4px',
                fontWeight: 600,
                marginLeft: '8px',
                background: ch.status === 'live' ? '#1a0a0a' : '#1a1a1a',
                color: ch.status === 'live' ? 'var(--live)' : '#666',
                border: ch.status === 'live' ? '1px solid #3a1a1a' : '1px solid #333'
              }}>
                {ch.status === 'live' ? '● LIVE' : '○ Offline'}
              </span>
              {ch.cat === 'worldcup' && ' 🏆'}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'var(--muted)',
              marginTop: '2px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '300px'
            }}>{ch.url || 'No URL set — click Edit'}</div>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleEdit(ch)}
              style={{
                background: '#0a2a1a',
                color: 'var(--accent2)',
                border: '1px solid #0a4a2a',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: '0.2s'
              }}
            >
              ✏️ Edit
            </button>
            <button
              onClick={() => handleDelete(ch.id)}
              style={{
                background: '#2a1010',
                color: '#ff6b6b',
                border: '1px solid #4a2020',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: '0.2s'
              }}
            >
              🗑
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderAddForm = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div style={{ marginBottom: '14px' }}>
          <label style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--muted)',
            display: 'block',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Channel Name</label>
          <input
            id="name"
            value={formData.name}
            onChange={handleInputChange}
            style={inputStyle}
            placeholder="e.g. T Sports"
          />
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--muted)',
            display: 'block',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Category</label>
          <select
            id="cat"
            value={formData.cat}
            onChange={handleInputChange}
            style={selectStyle}
          >
            <option value="worldcup">🏆 World Cup</option>
            <option value="sports">⚽ Sports</option>
            <option value="news">📰 News</option>
            <option value="entertainment">🎬 Entertainment</option>
            <option value="religious">🕌 Religious</option>
            <option value="kids">👶 Kids</option>
            <option value="movies">🎥 Movies</option>
            <option value="music">🎵 Music</option>
            <option value="infotainment">📺 Infotainment</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div style={{ marginBottom: '14px' }}>
          <label style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--muted)',
            display: 'block',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Stream URL (M3U8 / MP4 / YouTube)</label>
          <input
            id="url"
            value={formData.url}
            onChange={handleInputChange}
            style={inputStyle}
            placeholder="https://... .m3u8"
          />
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--muted)',
            display: 'block',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Icon (URL or Emoji)</label>
          <input
            id="icon"
            value={formData.icon}
            onChange={handleInputChange}
            style={inputStyle}
            placeholder="https://...logo.png or 📺"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div style={{ marginBottom: '14px' }}>
          <label style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--muted)',
            display: 'block',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Description</label>
          <input
            id="desc"
            value={formData.desc}
            onChange={handleInputChange}
            style={inputStyle}
            placeholder="e.g. Bangladesh's #1 Sports Channel"
          />
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--muted)',
            display: 'block',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Status</label>
          <select
            id="status"
            value={formData.status}
            onChange={handleInputChange}
            style={selectStyle}
          >
            <option value="live">🔴 Live</option>
            <option value="offline">⚫ Offline</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '14px' }}>
        <label style={{
          fontSize: '12px',
          fontWeight: 600,
          color: 'var(--muted)',
          display: 'block',
          marginBottom: '6px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>Stream Type</label>
        <select
          id="type"
          value={formData.type}
          onChange={handleInputChange}
          style={selectStyle}
        >
          <option value="hls">HLS (.m3u8) — Recommended</option>
          <option value="mp4">Direct MP4</option>
          <option value="iframe">iFrame / YouTube / External</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={handleSubmit}
          style={{
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: '0.2s'
          }}
        >
          {editingId ? '💾 Save Changes' : '➕ Add Channel'}
        </button>
        {editingId && (
          <button
            onClick={handleCancelEdit}
            style={{
              background: 'transparent',
              color: 'var(--muted)',
              border: '1px solid var(--border)',
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: '0.2s'
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </>
  );

  const renderStats = () => (
    <>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '14px',
        marginBottom: '20px'
      }}>
        <div style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <div style={{
            fontFamily: "'Teko', sans-serif",
            fontSize: '32px',
            fontWeight: 700,
            color: 'var(--accent)'
          }}>{stats.total}</div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>Total Channels</div>
        </div>
        <div style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <div style={{
            fontFamily: "'Teko', sans-serif",
            fontSize: '32px',
            fontWeight: 700,
            color: 'var(--live)'
          }}>{stats.live}</div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>Live Now</div>
        </div>
        <div style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <div style={{
            fontFamily: "'Teko', sans-serif",
            fontSize: '32px',
            fontWeight: 700,
            color: 'var(--worldcup-glow)'
          }}>{stats.worldcup}</div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>World Cup Channels</div>
        </div>
        <div style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <div style={{
            fontFamily: "'Teko', sans-serif",
            fontSize: '32px',
            fontWeight: 700,
            color: 'var(--accent2)'
          }}>{stats.cats}</div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>Categories</div>
        </div>
      </div>

      <div style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '16px',
        marginBottom: '16px',
        fontSize: '13px',
        color: 'var(--muted)',
        lineHeight: '1.7'
      }}>
        <strong style={{ color: 'var(--text)' }}>📊 About Stats:</strong><br />
        Real viewer tracking needs a backend server. For full analytics, connect this frontend to a Node.js or PHP backend with WebSocket support.
      </div>
    </>
  );

  const renderGuide = () => (
    <div style={{
      background: 'var(--card)',
      border: '1px solid var(--border)',
      borderRadius: '10px',
      padding: '16px',
      fontSize: '13px',
      color: 'var(--muted)',
      lineHeight: '1.7'
    }}>
      <strong style={{ color: 'var(--text)' }}>🎬 How to Get Stream URLs (M3U8)</strong><br /><br />

      <strong>Option 1 — OBS Studio (Free, Recommended):</strong><br />
      Install OBS → Settings → Stream → Choose "Custom" → Enter your RTMP server URL → Start Streaming. Your server converts RTMP → HLS (.m3u8).<br /><br />

      <strong>Option 2 — Restream a YouTube Live:</strong><br />
      Go to YouTube Live → Copy the embed URL → Add as <code style={{
        background: 'rgba(230,57,70,.15)',
        color: 'var(--accent)',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '12px'
      }}>iframe</code> type in Add Channel.<br /><br />

      <strong>Option 3 — M3U8 Playlist (IPTV):</strong><br />
      Get .m3u8 URLs from your IPTV provider. Paste as <code style={{
        background: 'rgba(230,57,70,.15)',
        color: 'var(--accent)',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '12px'
      }}>HLS</code> type. The player handles it automatically.<br /><br />

      <strong>⚠️ CORS:</strong> Some m3u8 URLs need a CORS proxy. Use <code style={{
        background: 'rgba(230,57,70,.15)',
        color: 'var(--accent)',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '12px'
      }}>https://cors-anywhere.herokuapp.com/YOUR_URL</code> if direct play fails.<br /><br />

      <strong>🔧 Backend Setup:</strong> For production, use <strong>Nginx + RTMP module</strong> on a VPS. OBS sends RTMP, Nginx serves HLS. Cost: ~$5/mo DigitalOcean.
    </div>
  );

  return (
    <div style={{
      paddingTop: '60px',
      minHeight: '100vh',
      background: 'var(--bg)',
      padding: '24px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <div style={{
            fontFamily: "'Teko', sans-serif",
            fontSize: '28px',
            fontWeight: 700,
            color: 'var(--accent)'
          }}>
            ⚙️ Admin Dashboard
            <span style={{
              fontSize: '14px',
              fontWeight: 400,
              color: 'var(--muted)',
              marginLeft: '12px'
            }}>
              /admin/{day}
            </span>
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer',
              transition: '0.2s'
            }}
          >
            ← Back to Player
          </button>
        </div>

        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '24px',
          borderBottom: '1px solid var(--border)',
          paddingBottom: '0',
          flexWrap: 'wrap'
        }}>
          {['channels', 'add', 'stats', 'guide'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'none',
                border: 'none',
                color: activeTab === tab ? 'var(--accent)' : 'var(--muted)',
                fontSize: '13px',
                fontWeight: 500,
                padding: '8px 14px',
                cursor: 'pointer',
                borderBottom: `2px solid ${activeTab === tab ? 'var(--accent)' : 'transparent'}`,
                transition: '0.2s',
                marginBottom: '-1px'
              }}
            >
              {tab === 'channels' && '📡 Channels'}
              {tab === 'add' && '➕ Add Channel'}
              {tab === 'stats' && '📊 Stats'}
              {tab === 'guide' && '📖 Guide'}
            </button>
          ))}
        </div>

        <div>
          {activeTab === 'channels' && renderChannelList()}
          {activeTab === 'add' && renderAddForm()}
          {activeTab === 'stats' && renderStats()}
          {activeTab === 'guide' && renderGuide()}
        </div>
      </div>

      <Toast message={toastMessage} />
    </div>
  );
};

const inputStyle = {
  width: '100%',
  background: 'var(--card)',
  border: '1px solid var(--border)',
  color: 'var(--text)',
  padding: '10px 12px',
  borderRadius: '8px',
  fontSize: '13px',
  outline: 'none',
  transition: '0.2s'
};

const selectStyle = {
  width: '100%',
  background: 'var(--card)',
  border: '1px solid var(--border)',
  color: 'var(--text)',
  padding: '10px 12px',
  borderRadius: '8px',
  fontSize: '13px',
  outline: 'none',
  transition: '0.2s'
};

export default AdminDashboard;